import css from './GoodsAndServices.module.scss'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useGet } from 'api'
import { v4 as uuidv4 } from 'uuid'

import { IGoodServiceTicketData } from 'meta/pages/goodsandservices'
import { RouteParams } from 'meta/routes'
import { ApiEndpoint } from 'controller/endpoint'

import Head from 'components/common/Head'
import WithAppInitialized from 'components/common/WithAppInitialized'
import withTranslation, { useI18NContext } from 'components/HOC/WithTranslation'
import Modal from 'components/Layout/Modal'
import Img from 'components/Media/Img'
import { useIsAppInitialized } from 'hooks/useApp'
import { useBackendApiBase } from 'hooks/useEnv'
import { useOnUpdate } from 'hooks/useOnUpdate'

import GSEventBox from './GSEventBox'
import GSEventBoxSkeleton from './GSEventBoxSkeleton'
import GSFullDescriptionModal from './GSFullDescriptionModal'
import GSQRCodeModal from './GSQRCodeModal'
import GSTicketCard from './GSTicketCard'
import GSTicketSkeleton from './GSTicketSkeleton'
import Tap2GoModal from './Tap2GOModal'

const GoodsAndServices: React.FC = () => {
  const { secret } = useParams<RouteParams['GoodsAndServices']>()

  const [showTap2GoModal, setShowTap2GoModal] = useState<boolean>(false)
  const [showQRCodeModal, setShowQRCodeModal] = useState<boolean>(false)
  const [initialUuid, setInitialUuid] = useState<string>('')
  const [showFullDescriptionModal, setShowFullDescriptionModal] = useState<boolean>(false)
  const [gsTicketData, setGsTicketData] = useState<IGoodServiceTicketData | null>(null)

  const { namespacesLoaded } = useI18NContext()
  const backendApiBase = useBackendApiBase()
  const appInitialized = useIsAppInitialized()

  const { fetch, response } = useGet()

  useEffect(() => {
    if (!appInitialized || !backendApiBase || !secret) return
    fetch({ urlResource: `${ApiEndpoint.tickets.secret(secret)}` })
  }, [appInitialized, backendApiBase, secret])

  useOnUpdate(() => {
    if (response) {
      setGsTicketData(response.data as IGoodServiceTicketData)
      setInitialUuid(uuidv4())
    }
  }, [response])

  return (
    <WithAppInitialized showChildren={namespacesLoaded}>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>

      <div className={css.wrapper}>
        <div className={css.logo}>
          <Img alt="TicketSMS Logo" src="/logo/TicketSms_logo.svg" />
        </div>

        {gsTicketData && (
          <>
            <GSEventBox data={gsTicketData} />

            <GSTicketCard
              data={gsTicketData}
              showTap2GoModal={setShowTap2GoModal}
              showQRCodeModal={setShowQRCodeModal}
              showFullDescriptionModal={setShowFullDescriptionModal}
            />

            <Img className={css.hidden} src="/goodsandservices/tap2go-error-bold.svg" />

            {showQRCodeModal && (
              <Modal
                closeIconPath="/icons/close_icon_white_smallcross.svg"
                transparent
                noBodyFixed
                show
                fullWidth
                limitDesktopWidth
                disableScroll
                handleClose={() => setShowQRCodeModal(false)}
              >
                <GSQRCodeModal data={gsTicketData} />
              </Modal>
            )}

            {showFullDescriptionModal && (
              <Modal
                closeIconPath="/icons/close_icon_white_smallcross.svg"
                transparent
                noBodyFixed
                show
                fullWidth
                limitDesktopWidth
                disableScroll
                handleClose={() => setShowFullDescriptionModal(false)}
              >
                <GSFullDescriptionModal data={gsTicketData} />
              </Modal>
            )}

            {showTap2GoModal && (
              <Modal
                show
                noBodyFixed
                hideCloseIcon
                fullWidth
                transparent
                limitDesktopWidth
                disableScroll
                handleClose={() => setShowTap2GoModal(false)}
              >
                <Tap2GoModal
                  initialUuid={initialUuid}
                  ticketData={gsTicketData}
                  onClose={() => {
                    setShowTap2GoModal(false)
                  }}
                />
              </Modal>
            )}
          </>
        )}

        {!gsTicketData && (
          <>
            <GSEventBoxSkeleton />

            <GSTicketSkeleton />
          </>
        )}
      </div>
    </WithAppInitialized>
  )
}

const namespaces = ['goodsAndServices']

export default withTranslation(namespaces)(GoodsAndServices)
